import {
  collection,
  deleteField,
  doc,
  getDocs,
  orderBy,
  query,
  runTransaction,
} from "firebase/firestore";
import type { SubmittedDocument } from "~/types/SubmittedDocument";
import { DocumentState } from "~/types/enums/DocumentState";
import { canReopenStudentDocument } from "~/utils/student-document-editing";

export const useReopenStudentDocument = async (documentId: string) => {
  const db = useFirestore();
  const documentRef = doc(db, `/documents/${documentId}`);
  const revisions = await getDocs(query(
    collection(db, `/documents/${documentId}/revisions`),
    orderBy("lastUpdatedTimestamp", "desc")
  ));
  const activeRevisions = revisions.docs.filter(d => d.data().isArchived !== true);
  const draft = activeRevisions.find(d => d.data().studentSubmissionOps != null);
  const submission = activeRevisions.find(d => d.data().studentSubmissionOps == null);

  if (!draft) {
    throw new Error("This submission has no editable draft. Please upload a new document instead.");
  }

  await runTransaction(db, async transaction => {
    const document = await transaction.get(documentRef);
    // Recheck after confirmation and on transaction retries, in case the teacher
    // opened the submission while the student was deciding to edit it.
    if (!document.exists() || !canReopenStudentDocument(document.data() as SubmittedDocument)) {
      throw new Error("This document can no longer be edited because grading has started or its status has changed.");
    }
    const draftSnapshot = await transaction.get(draft.ref);
    const submissionSnapshot = submission ? await transaction.get(submission.ref) : undefined;
    if (!draftSnapshot.exists() || draftSnapshot.data().isArchived === true) {
      throw new Error("The editable draft is no longer available. Please refresh and try again.");
    }
    if (submissionSnapshot?.exists()) {
      transaction.update(submission!.ref, { isArchived: true });
    }
    transaction.update(documentRef, {
      state: DocumentState.editing,
      lastUpdatedTimestamp: Date.now(),
      submittedAtTimestamp: deleteField(),
      submittedAtTimestampMillis: deleteField(),
      studentGuidedReviewStepIndex: deleteField(),
      submissionSeal: deleteField(),
      badge: deleteField(),
      isLate: false,
    });
  });
};
